import React, { useContext, useState } from "react";
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from "../authProvider.mjs";
import seedlogo from "../../src/assets/seedlogo.jpg";
import BackButton from "./BackButton.mjs";
import { isTestEnvironment } from "../utils/environment.mjs";
import ClarificationBell from "../clarifications/components/ClarificationBell.mjs";

// One set of links for every width. Below md they sit in a drop-down panel
// opened by the menu button; at md and up the panel becomes `display: contents`
// so the same elements lay out inline (Home left, account actions right). A
// separate mobile copy would double every label for screen readers.
//
// menuExtra: rendered at the foot of the phone menu panel only (App passes the
// test-environment role switcher, which on a phone has no room to float).
const Navbar = ({ menuExtra = null }) => {
  const { isAuthenticated, user: _user, logout } = useContext(AuthContext);
  const navigate = useNavigate()
  const [menuOpen, setMenuOpen] = useState(false);

  const handleProfile = () => {
    return navigate("/profile")
  }

  const itemClass = "block px-3 py-3 md:py-2 rounded-lg text-base md:text-sm font-medium transition-colors";

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white shadow-md">
      <div className="relative h-16 px-4 md:px-6 flex items-center justify-between">
        {/* Back (and on phones the TEST tag) on the left; the logo is centred
            in the bar at every width (the user, 2026-10-21: centred on mobile
            too), clear of them and of the menu button. */}
        <div className="flex items-center gap-1 md:order-first">
          <BackButton />
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center">
            {/* The logo art has a white ground; in dark mode it reads as a
                deliberate plate rather than a stray white box. */}
            <img src={seedlogo} alt="Stanford Seed" className="h-9 md:h-10 w-auto dark:rounded-md" />
          </div>
          {/* Phones: the test-environment tag lives here rather than pinned
              over the bottom of the page (TestEnvironmentBanner, md and up). */}
          {isTestEnvironment() && (
            <span
              className="md:hidden ml-1 px-2 py-0.5 rounded-full bg-amber-700 text-white text-[10px] font-bold tracking-wide leading-4"
              title="Test environment"
            >
              TEST
            </span>
          )}
        </div>

        <button
          type="button"
          className="md:hidden inline-flex items-center justify-center w-11 h-11 -mr-2 rounded-lg text-gray-700 hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-stanford-red"
          aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={menuOpen}
          aria-controls="primary-nav-links"
          onClick={() => setMenuOpen((o) => !o)}
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            {menuOpen
              ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7h16M4 12h16M4 17h16" />}
          </svg>
        </button>

        {/* Any click inside (a link or a button) closes the panel, so it never
            stays open over the page it just navigated to. */}
        <div
          id="primary-nav-links"
          onClick={() => setMenuOpen(false)}
          className={`${menuOpen ? 'flex' : 'hidden'} md:contents flex-col gap-1
            absolute top-16 left-0 right-0 p-3 bg-white border-t border-gray-100 shadow-lg`}
        >
          <Link to="/" className={`${itemClass} md:order-first text-stanford-red hover:text-stanford-light-red hover:bg-gray-50 md:hover:bg-transparent`}>Home Page</Link>

          {isAuthenticated ? (
            <>
              <button
                type="button"
                className={`${itemClass} md:ml-auto text-left text-gray-700 hover:text-stanford-red hover:bg-gray-50 md:hover:bg-transparent`}
                onClick={() => {handleProfile()}}
              >
                <span>Profile</span>
              </button>

              {/* Always there for staff; for a claimant only while a question
                  is waiting on them. After Profile so Profile keeps the
                  md:ml-auto that pushes the account links right whether or
                  not the bell is there. */}
              <ClarificationBell className={`${itemClass} md:ml-1`} />

              <button
                onClick={logout}
                className="mt-1 md:mt-0 md:ml-3 px-4 py-3 md:py-2 bg-stanford-red text-white rounded-lg hover:bg-stanford-light-red transition-colors font-medium text-base md:text-sm"
              >
                Logout
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className={`${itemClass} md:ml-auto text-gray-700 hover:text-stanford-red hover:bg-gray-50 md:hover:bg-transparent`}>Login</Link>
              <Link to="/signup" className="mt-1 md:mt-0 md:ml-3 px-4 py-3 md:py-2 text-center bg-stanford-red text-white rounded-lg hover:bg-stanford-light-red transition-colors font-medium text-base md:text-sm">Sign Up</Link>
            </>
          )}

          {/* Stops the panel's close-on-click, or opening the select would
              shut the menu before a choice could be made. */}
          {menuExtra && (
            <div className="md:hidden" onClick={(e) => e.stopPropagation()}>
              {menuExtra}
            </div>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
